// src/lib/auth.js
// STATUS: stable | UPDATED: 2026-08-02
// LOG:
//   2026-08-02  Header standardized; Phase 1 checkpoint committed (e765830).
// Auth helpers. signInWithIdentifier accepts either a username or an email.
// Password reset uses Supabase's built-in flow with a custom redirect URL.
//
// (2026-07-21) Added verifyCurrentPassword and changeEmail to power the
// Settings page: re-verify the current password before sensitive changes,
// and change the account email (Supabase confirms via the new address).

import { supabase } from './supabase'

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/

async function lookupEmailByUsername(username) {
  try {
    const response = await fetch('/.netlify/functions/lookup-email', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ username }),
    })
    if (!response.ok) {
      return { email: null, error: 'Lookup failed' }
    }
    const data = await response.json()
    if (!data.found) {
      return { email: null, error: null }
    }
    return { email: data.email, error: null }
  } catch (err) {
    console.error('lookupEmailByUsername error:', err)
    return { email: null, error: 'Network error' }
  }
}

export async function signInWithIdentifier(identifier, password) {
  const trimmed = (identifier || '').trim()
  if (!trimmed || !password) {
    return { data: null, error: { message: 'Username and password required.' } }
  }

  let email = trimmed
  if (!EMAIL_REGEX.test(trimmed)) {
    const { email: lookedUp, error: lookupError } = await lookupEmailByUsername(trimmed)
    if (lookupError) {
      return { data: null, error: { message: 'Unable to verify credentials. Try again.' } }
    }
    if (!lookedUp) {
      return { data: null, error: { message: 'Invalid username or password.' } }
    }
    email = lookedUp
  }

  const { data, error } = await supabase.auth.signInWithPassword({ email, password })
  if (error) {
    return { data, error: { message: 'Invalid username or password.' } }
  }
  return { data, error: null }
}

export async function signInWithPassword(email, password) {
  return signInWithIdentifier(email, password)
}

export async function signUpWithPassword(email, password, metadata = {}) {
  const { data, error } = await supabase.auth.signUp({
    email,
    password,
    options: { data: metadata },
  })
  return { data, error }
}

export async function sendPasswordReset(email) {
  const trimmed = (email || '').trim()
  if (!EMAIL_REGEX.test(trimmed)) {
    return { error: { message: 'Please enter a valid email address.' } }
  }

  const redirectTo = `${window.location.origin}/reset-password/`
  const { data, error } = await supabase.auth.resetPasswordForEmail(trimmed, {
    redirectTo,
  })
  return { data, error }
}

export async function updatePassword(newPassword) {
  const { data, error } = await supabase.auth.updateUser({ password: newPassword })
  return { data, error }
}

// Verify the current password by attempting a silent re-auth with the account's
// own email. Returns { ok, error }. Used to unlock sensitive Settings actions
// (change email, change password) so a walk-up cannot change them.
export async function verifyCurrentPassword(currentPassword) {
  const { data: { user } } = await supabase.auth.getUser()
  if (!user?.email) {
    return { ok: false, error: { message: 'Not signed in.' } }
  }
  const { error } = await supabase.auth.signInWithPassword({
    email: user.email,
    password: currentPassword,
  })
  if (error) {
    return { ok: false, error: { message: 'Current password is incorrect.' } }
  }
  return { ok: true, error: null }
}

// Change email. Supabase sends a confirmation link to the NEW address; the
// change only takes effect once the user clicks it. Callers should verify the
// current password first (see verifyCurrentPassword).
export async function changeEmail(newEmail) {
  const trimmed = (newEmail || '').trim().toLowerCase()
  if (!EMAIL_REGEX.test(trimmed)) {
    return { data: null, error: { message: 'Please enter a valid email address.' } }
  }
  const { data, error } = await supabase.auth.updateUser({ email: trimmed })
  return { data, error }
}

export async function signOut() {
  const { error } = await supabase.auth.signOut()
  return { error }
}

export async function getCurrentUser() {
  const { data: { user } } = await supabase.auth.getUser()
  return user
}

// Never throws and always settles.
//
// A returning visitor holds a refresh token, so getSession may go to the
// network to trade it in. That call can stall, and it rejects on a network
// blip. Either one used to leave AuthProvider's setLoading(false) unreached and
// the app spinning until somebody hit reload, which is exactly the symptom
// Tyler hit on academy.jandatraining.com. A cold visitor never saw it because
// there was no token to refresh.
//
// Same shape as the bound already proven in progressStore.hydrateProgress. Six
// seconds rather than three: showing the login screen to somebody who is
// actually signed in is worse than a slightly longer spinner, and the auth
// listener still delivers the session when the refresh lands.
export async function getCurrentSession() {
  const timeout = new Promise((resolve) => setTimeout(() => resolve('timeout'), 6000))
  try {
    const result = await Promise.race([supabase.auth.getSession(), timeout])
    if (result === 'timeout') {
      console.warn('auth: session restore timed out, waiting on the auth listener')
      return null
    }
    return result?.data?.session ?? null
  } catch (err) {
    console.error('auth: session restore failed', err.message)
    return null
  }
}

export function onAuthStateChange(callback) {
  return supabase.auth.onAuthStateChange(callback)
}

// Update the parts of a profile a person is allowed to change about themselves.
//
// Deliberately a allow-list of two fields rather than a passthrough. RLS lets a
// user update their own row, and the row also carries role, dealership_id and
// job_function, which decide what they can see and do. A generic
// updateProfile(patch) would make privilege escalation a typo away, so the
// fields that define permission are simply not reachable from here.
export async function updateOwnProfile(userId, { display_name, initials }) {
  const patch = { updated_at: new Date().toISOString() }
  if (display_name !== undefined) patch.display_name = display_name.trim() || null
  if (initials !== undefined) patch.initials = initials.trim().toUpperCase().slice(0, 3) || null

  const { data, error } = await supabase
    .from('users')
    .update(patch)
    .eq('id', userId)
    .select()
    .single()
  return { data, error }
}

// Never throws and always settles, for the same reason as getCurrentSession:
// it is awaited on the startup path in front of the loading gate.
export async function getUserProfile(userId) {
  const timeout = new Promise((resolve) => setTimeout(() => resolve('timeout'), 6000))
  try {
    const result = await Promise.race([
      supabase.from('users').select('*, dealership:dealerships(*)').eq('id', userId).single(),
      timeout,
    ])
    if (result === 'timeout') {
      console.warn('auth: profile load timed out')
      return { data: null, error: new Error('timeout') }
    }
    return { data: result?.data ?? null, error: result?.error ?? null }
  } catch (err) {
    console.error('auth: profile load failed', err.message)
    return { data: null, error: err }
  }
}